import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { Command as CommandPrimitive } from 'cmdk'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../ui/command'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { httpClient } from '../../lib/httpClient'

export interface LinkedProduct {
  product_id: string
  title: string
}

/**
 * `linked_products` field input — a multi-product picker against `GET
 * /products?query=`, base support for this `field_type` (review on
 * HATCH-358: "Lets move LinkedProductsInput support to DynamicFormRenderer
 * - so it's basically base support for linked_products field_type", then
 * "Put LinkedProductsInput to separate file" — this component's own doc
 * comment in `DynamicFormRenderer.tsx` already carved `linked_products` out
 * as needing "its own bespoke upload/picker primitive"; this is that
 * primitive, promoted to a real file once a real caller needed it). A
 * field-specific special case (like `apps/specialists-portal`'s
 * `prescription_products`, which needs a doctor-scoped Prescription Builder
 * instead) stays a `renderField` override in the caller, not a fork of this
 * one.
 *
 * Selected products are tracked locally in `productsById`, seeded directly
 * from the search result being added (or nothing, for a removal) instead of
 * re-`useQuery`-ing `GET /products?product_id=...` keyed by the whole
 * selected-id array: a fresh array identity on every add/remove would mint
 * a new query key each time, flashing the list empty while that refetch is
 * in flight. The one real "resolve ids to product info" fetch only ever
 * runs once, hydrating ids that arrived from the field's initial value (a
 * page load/reload) rather than from a search pick.
 */
export function LinkedProductsInput({
  value,
  onChange,
}: {
  value: string[]
  onChange: (value: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [productsById, setProductsById] = useState<Record<string, LinkedProduct>>({})

  const unresolvedIds = value.filter((id) => !productsById[id])
  useQuery({
    queryKey: ['products', 'getMany', 'linked_products_input', unresolvedIds.join(',')],
    queryFn: async () => {
      const found = await httpClient<LinkedProduct[]>(
        `/products?${unresolvedIds.map((id) => `product_id=${id}`).join('&')}`,
      )
      setProductsById((prev) => {
        const next = { ...prev }
        for (const product of found) next[product.product_id] = product
        return next
      })
      return found
    },
    enabled: unresolvedIds.length > 0,
  })

  const { data: matches, isFetching } = useQuery({
    queryKey: ['products', 'search', search],
    queryFn: () => httpClient<LinkedProduct[]>(`/products?query=${encodeURIComponent(search)}&limit=10`),
    enabled: open && search.length > 0,
  })

  const addProduct = (product: LinkedProduct) => {
    setProductsById((prev) => ({ ...prev, [product.product_id]: product }))
    if (!value.includes(product.product_id)) onChange([...value, product.product_id])
    setOpen(false)
    setSearch('')
  }

  const removeProduct = (productId: string) => {
    onChange(value.filter((id) => id !== productId))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {value.map((id) => {
          const product = productsById[id]
          return (
            <Badge key={id} variant="secondary" className="gap-1">
              {product?.title ?? id}
              <button type="button" onClick={() => removeProduct(id)} aria-label={`Remove ${product?.title ?? id}`}>
                <XIcon className="size-3" />
              </button>
            </Badge>
          )
        })}
      </div>
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => setOpen(true)}>
        Add product
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        {/* Pinned near the top instead of the default vertical-centering
            (ticket-comment feedback: "add product button renders
            dropdown/search input under keyboard on mobile") — a centered
            dialog whose own search input autofocuses (and so immediately
            opens the on-screen keyboard) ends up with that input sitting
            behind the keyboard, since the dialog's center is computed
            against the full layout viewport, not the shrunk visual one the
            keyboard leaves visible. */}
        <DialogContent className="top-4 max-h-[calc(100dvh-2rem)] translate-y-0 overflow-y-auto p-0">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle>Add product</DialogTitle>
          </DialogHeader>
          <Command shouldFilter={false}>
            <CommandPrimitive.Input
              className="border-b px-3 py-2 text-sm outline-none"
              placeholder="Search products…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {!isFetching && search.length > 0 && (matches ?? []).length === 0 ? (
                <CommandEmpty>No products found.</CommandEmpty>
              ) : null}
              <CommandGroup>
                {(matches ?? []).map((product) => (
                  <CommandItem key={product.product_id} value={product.product_id} onSelect={() => addProduct(product)}>
                    {product.title}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  )
}
